import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import { products } from '../data/products';
import { Button } from '../components/ui/Button';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Accordion } from '../components/ui/Accordion';
import { MockDashboard } from '../components/ui/MockDashboard';
export function ProductDetailPage() {
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const product = products.find(p => p.id === productId);
  if (!product) {
    return <Navigate to="/products" replace data-id="element-417" />;
  }
  const Icon = product.icon;
  // Map product ID to dashboard variant
  const getDashboardVariant = (id: string) => {
    if (id.includes('budget')) return 'finance';
    if (id.includes('cooler')) return 'hr';
    if (id.includes('brand')) return 'brand';
    if (id.includes('price')) return 'pricing';
    if (id.includes('legal')) return 'legal';
    if (id.includes('paper')) return 'docs';
    return 'main';
  };
  return <div className="pt-24" data-id="element-418">
      {/* Hero */}
      <section className="pb-20 pt-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-419">
        <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-420">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-421">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-6 bg-${product.color}-100 text-${product.color}-700`} data-id="element-422">
              <Icon size={16} className="mr-2" data-id="element-423" />
              {product.name}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-424">
              {product.tagline}
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed" data-id="element-425">
              {product.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-426">
              <Button size="lg" href="/contact" data-id="element-427">
                Request Demo
              </Button>
              <Button variant="outline" size="lg" href="/pricing" data-id="element-428">
                View Pricing
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-429">
            <MockDashboard variant={getDashboardVariant(product.id) as any} data-id="element-430" />
          </motion.div>
        </div>
      </section>

      {/* Problems Solved */}
      <section className="py-20 bg-slate-50" data-id="element-431">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-432">
          <SectionHeading title="Challenges we solve" description="Stop struggling with manual processes and disconnected data." className="mb-12" data-id="element-433" />

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-434">
            {product.problemsSolved.map((problem, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex items-start p-6 bg-white rounded-xl border border-slate-100 shadow-sm" data-id="element-435">
                <div className="bg-red-50 text-red-500 p-2 rounded-lg mr-4 flex-shrink-0" data-id="element-436">
                  <ArrowRight size={20} className="rotate-45" data-id="element-437" />
                </div>
                <p className="text-lg text-slate-700 font-medium" data-id="element-438">{problem}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-white" data-id="element-439">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-440">
          <SectionHeading title="Key Features" className="mb-16" data-id="element-441" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-442">
            {product.features.map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:border-navy-200 transition-colors" data-id="element-443">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 bg-${product.color}-100 text-${product.color}-600`} data-id="element-444">
                  <Check size={20} data-id="element-445" />
                </div>
                <h3 className="font-bold text-slate-900 text-lg" data-id="element-446">{feature}</h3>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-20 bg-slate-900 text-white" data-id="element-447">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-448">
          <SectionHeading title="Who is this for?" light className="mb-16" data-id="element-449" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-450">
            {product.targetAudience.map((role, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-slate-800 p-8 rounded-2xl text-center border border-slate-700" data-id="element-451">
                <p className="text-xl font-semibold" data-id="element-452">{role}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white" data-id="element-453">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-454">
          <SectionHeading title="Frequently Asked Questions" className="mb-12" data-id="element-455" />
          <Accordion items={product.faqs} data-id="element-456" />
        </div>
      </section>

      {/* Final CTA */}
      <section className={`py-24 bg-${product.color}-600 text-white text-center`} data-id="element-457">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-458">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-459">
            Start using {product.name} today
          </h2>
          <Button variant="secondary" size="lg" href="/contact" data-id="element-460">
            Request a Demo
          </Button>
        </div>
      </section>
    </div>;
}